import React, { FunctionComponent } from 'react';
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  IInvoice,
  INVOICE_STATUSES,
} from '../../../../../../store/V2/InvoicesSlice';
import { useAppSelector } from '../../../../../../hooks';
import { getTenantsState } from '../../../../../../store/V2/TenantsSlice';
import { getAppBaseUrlForTenant } from '../../../../../../helper';

interface ViewInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  invoice: IInvoice;
}

interface ICustomCardProps {
  title?: string;
  description?: string;
  type?: 'primary' | 'secondary';
}

const CustomCard = ({
  title,
  description,
  type = 'primary',
}: ICustomCardProps) => {
  let bgColor = '#F8F9FD';
  let fontWeight = 600;

  const color = '#1E293B';

  if (type === 'secondary') {
    bgColor = '#fbfbfb';
    fontWeight = 500;
  }

  return (
    <Card elevation={0} sx={{ backgroundColor: bgColor, borderRadius: '7px' }}>
      <CardContent style={{ padding: '10px 16px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {title ? (
            <Typography fontSize="14px" fontWeight={fontWeight} color={color}>
              {title}
            </Typography>
          ) : (
            <div />
          )}
          {description ? (
            <Typography fontSize="14px" fontWeight={fontWeight} color={color}>
              {description}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ViewInvoiceModal: FunctionComponent<ViewInvoiceModalProps> = ({
  open,
  onClose,
  invoice,
}) => {
  // Store
  const tenantsState = useAppSelector(getTenantsState);

  const { appTenant } = tenantsState;

  const invoiceNumber = invoice?.invoiceNumber.toString().padStart(4, '0');

  const listItems: ICustomCardProps[] = [];
  const paymentLink = `${getAppBaseUrlForTenant(appTenant)}${
    process.env.PUBLIC_URL
  }/${process.env.REACT_APP_PAY_INVOICE_PATH}/${invoice._id}`;

  let total = 0;
  let statusColor = 'warning.main';

  if (invoice.status === INVOICE_STATUSES.PAID) {
    statusColor = 'success.main';
  } else if (invoice.status === INVOICE_STATUSES.VOID) {
    statusColor = 'secondary.main';
  }

  if (invoice.isCreditChecked && invoice.creditRate && invoice.creditQuantity) {
    total += invoice.creditRate * invoice.creditQuantity;

    listItems.push({
      title: invoice.creditDescription,
      description: '',
    });

    listItems.push({
      title: 'Quantity (Hours)',
      description: invoice.creditQuantity.toString(),
      type: 'secondary',
    });

    listItems.push({
      title: 'Rate',
      description: `${invoice.currency} ${invoice.creditRate}`,
      type: 'secondary',
    });
  }

  if (
    invoice.isCustomChecked &&
    invoice.customAmounts &&
    invoice.customAmounts.length
  ) {
    invoice.customAmounts.forEach((item) => {
      total += item.quantity * item.rate;

      listItems.push({
        title: item.description,
        description: '',
      });

      listItems.push({
        title: 'Quantity',
        description: item.quantity.toString(),
        type: 'secondary',
      });

      listItems.push({
        title: 'Rate',
        description: `${invoice.currency} ${item.rate}`,
        type: 'secondary',
      });
    });
  }

  listItems.push({
    title: '',
    description: `Total ${invoice.currency} ${total}`,
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Invoice No INV-{invoiceNumber}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <Stack mb="20px">
          <div>
            <Typography
              fontSize="12px"
              color={statusColor}
              sx={{ textTransform: 'capitalize' }}
            >
              Status: {invoice.status.toLowerCase()}
            </Typography>
          </div>
          {invoice.stripeSessionId ? (
            <div>
              <Typography fontSize="12px" color="#707070">
                Reference: {invoice.stripeSessionId}
              </Typography>
            </div>
          ) : null}
          <div>
            <Typography fontSize="12px" color="#707070">
              Created At:{' '}
              {moment(invoice.createdAt).format('ddd MMM DD YYYY hh:mm A')}
            </Typography>
          </div>
        </Stack>
        <div>
          <Grid container spacing={1}>
            {listItems.map((item, index) => (
              <Grid item xs={12} key={`li-item-${index}`}>
                <CustomCard {...item} />
              </Grid>
            ))}
          </Grid>
        </div>
        {invoice.status === INVOICE_STATUSES.PENDING ? (
          <div style={{ paddingTop: '20px' }}>
            <div>
              <Typography variant="h6">Payment Link</Typography>
            </div>
            <div>
              <Link
                component="a"
                variant="caption"
                href={paymentLink}
                target="_blank"
              >
                {paymentLink}
              </Link>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ViewInvoiceModal;
